import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import taxCouponServices from './coupons.services';
import notificationError from '@/utils/functions/notificationError';
import { TaxCouponDetailRequest, TaxCouponListRequest } from './coupons.types';
import { notification } from 'antd';
import { couponDicionaryError } from './coupon.dicionary';

const TAX_COUPON_LIST_QUERY_KEY = 'taxtCoupon/list';
const TAX_COUPON_DETAIL_QUERY_KEY = 'taxtCoupon/detail';
const TAX_COUPON_ITEMS_QUERY_KEY = 'taxtCoupon/items';

export function useTaxCouponList(params: TaxCouponListRequest) {
  return useInfiniteQuery(
    [TAX_COUPON_LIST_QUERY_KEY, { ...params }],
    ({ pageParam }) => {
      return taxCouponServices.list({ ...params, page: pageParam });
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.pagination.page < lastPage.pagination.totalPages
          ? lastPage.pagination.page + 1
          : undefined,
      enabled: !!params.companyId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useTaxCouponDetail(params: TaxCouponDetailRequest) {
  return useQuery(
    [TAX_COUPON_DETAIL_QUERY_KEY, { ...params }],
    () => taxCouponServices.detail(params),
    {
      enabled: !!params.taxCouponId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useTaxCouponCreate() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.create, {
    async onSuccess() {
      notification.success({ message: 'Cupom cadastrado com sucesso!' });
      await queryClient.invalidateQueries([TAX_COUPON_LIST_QUERY_KEY]);
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponCancel() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.cancel, {
    async onSuccess(_, taxCouponId) {
      notification.success({ message: 'Cupom cancelado com sucesso!' });
      await queryClient.invalidateQueries([
        TAX_COUPON_DETAIL_QUERY_KEY,
        { taxCouponId },
      ]);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponRemove() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.remove, {
    async onSuccess() {
      notification.success({ message: 'Cupom removido com sucesso!' });
      await queryClient.invalidateQueries([TAX_COUPON_LIST_QUERY_KEY]);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponPaymentCreate() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.paymentCreate, {
    async onSuccess(_, { taxCouponId }) {
      await queryClient.invalidateQueries([
        TAX_COUPON_DETAIL_QUERY_KEY,
        { taxCouponId },
      ]);

      notification.success({
        message: 'Método de pagamento cadastrado com sucesso!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponPaymentEdit() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.paymentEdit, {
    async onSuccess(_, { taxCouponId }) {
      await queryClient.invalidateQueries([
        TAX_COUPON_DETAIL_QUERY_KEY,
        { taxCouponId },
      ]);

      notification.success({
        message: 'Método de pagamento editado com sucesso!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponPaymentRemove() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.paymentRemove, {
    async onSuccess(_, { taxCouponId }) {
      await queryClient.invalidateQueries([
        TAX_COUPON_DETAIL_QUERY_KEY,
        { taxCouponId },
      ]);

      notification.success({
        message: 'Método de pagamento removido com sucesso!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponItemList(taxCouponId?: string) {
  return useQuery(
    [TAX_COUPON_ITEMS_QUERY_KEY, { taxCouponId }],
    () => taxCouponServices.itemList(taxCouponId),
    {
      enabled: !!taxCouponId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useTaxCouponItemCreate() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.itemCreate, {
    async onSuccess(_, data) {
      await queryClient.invalidateQueries([
        TAX_COUPON_ITEMS_QUERY_KEY,
        { taxCouponId: data.taxCouponId },
      ]);

      await queryClient.invalidateQueries([
        TAX_COUPON_DETAIL_QUERY_KEY,
        { taxCouponId: data.taxCouponId },
      ]);

      notification.success({
        message: 'Itens do cupom cadastrados com sucesso!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponItemEdit() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.itemEdit, {
    async onSuccess(_, data) {
      await queryClient.invalidateQueries([
        TAX_COUPON_ITEMS_QUERY_KEY,
        { taxCouponId: data.taxCouponId },
      ]);

      await queryClient.invalidateQueries([
        TAX_COUPON_DETAIL_QUERY_KEY,
        { taxCouponId: data.taxCouponId },
      ]);

      notification.success({
        message: 'Item do cupom editado com sucesso!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponItemRemove() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.itemRemove, {
    async onSuccess(_, data) {
      await queryClient.invalidateQueries([
        TAX_COUPON_ITEMS_QUERY_KEY,
        { taxCouponId: data.taxCouponId },
      ]);

      await queryClient.invalidateQueries([
        TAX_COUPON_DETAIL_QUERY_KEY,
        { taxCouponId: data.taxCouponId },
      ]);

      notification.success({
        message: 'Item do cupom removido com sucesso!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponRecipienteCreate() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.recipientCreate, {
    async onSuccess(_, data) {
      await queryClient.invalidateQueries([
        TAX_COUPON_DETAIL_QUERY_KEY,
        { taxCouponId: data.taxCouponId },
      ]);

      notification.success({
        message: 'Destinatário do cupom salvo com sucesso!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useTaxCouponGenerateTx2(showNotification = true) {
  return useMutation(taxCouponServices.generateTx2, {
    onSuccess() {
      if (showNotification) {
        notification.success({
          message: 'Arquivo gerado com sucesso!',
        });
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError(error: any) {
      if (error.isAxiosError) {
        error = {
          ...error,
          response: {
            ...error.response,
            data: {
              ...error.response.data,
              message: error.response.data.msg
                ? error.response.data.msg
                : error.response.data.message,
            },
          },
        };
      }

      notificationError(error, {
        translate: (errors) =>
          errors.map(
            (err) =>
              couponDicionaryError[
                Object.keys(couponDicionaryError).find((key) =>
                  err.includes(key),
                ) as string
              ] || err,
          ),
      });
    },
  });
}

export function useTaxCouponUpdateCode(showNotification = true) {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.updateCode, {
    async onSuccess(_, data) {
      await queryClient.invalidateQueries([
        TAX_COUPON_DETAIL_QUERY_KEY,
        { taxCouponId: data.couponId },
      ]);

      if (showNotification) {
        notification.success({
          message: 'Cupom atualizado com sucesso!',
        });
      }
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCouponWorkorderCreate() {
  const queryClient = useQueryClient();

  return useMutation(taxCouponServices.workorderCreate, {
    async onSuccess() {
      notification.success({ message: 'Cupom cadastrado com sucesso!' });
      await queryClient.invalidateQueries([TAX_COUPON_LIST_QUERY_KEY]);
    },
    onError(error) {
      notificationError(error);
    },
  });
}
